import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconTextShort } from '@components/icons/text-short';
import { TextField } from '@components/text-field';
import { getScopedTranslate } from '@locale/translate';
import { asteriskLabel } from '@utils/strings';

import { FieldDropzone } from './field-dropzone';
import { useTextInputActions } from './use-field-actions';

import { conditionsTab } from '../conditions-tab';
import { useFormBuilderContext } from '../context';
import { DefaultValueMapping, DefaultValueMappingDisplay } from '../default-value-mapping';
import { DescriptionInput } from '../description-input';
import { FieldEdit } from '../field-edit';
import { FieldConfig } from '../field-registry';
import { FieldSidebarLabel } from '../fields-sidebar';
import { RequiredSwitch } from '../required-switch';

type Config = FieldConfig<'input-text-small'>;

const Display: Config['Display'] = ({ field }) => {
  return (
    <DefaultValueMappingDisplay field={field}>
      <TextField
        label={asteriskLabel(field.label, field.isRequired)}
        description={field.description}
        placeholder={field.placeholder}
        labelIcon={field.companyAttributeId ? <IconAttributeTraveller size={16} /> : undefined}
      />
    </DefaultValueMappingDisplay>
  );
};

const FormBuilderTextSmall: Config['Component'] = ({ field, dragHandle }) => {
  const actions = useFormBuilderContext().store.actions;
  const menuItems = useTextInputActions(field);

  return (
    <FieldDropzone field={field}>
      <FieldEdit
        field={field}
        dragHandle={dragHandle}
        menu={menuItems}
        display={<Display field={field} />}
        edit={
          <>
            <TextField
              onChange={actions.createChangeHandler(field, 'label')}
              defaultValue={field.label}
              label={t('label')}
              maxChars={255}
            />
            <DescriptionInput field={field} />
            <TextField
              onChange={actions.createChangeHandler(field, 'placeholder')}
              defaultValue={field.placeholder}
              label={t('placeholder')}
            />
            <RequiredSwitch field={field} />
            <DefaultValueMapping field={field} />
          </>
        }
      />
    </FieldDropzone>
  );
};

const { t } = getScopedTranslate({
  en: {
    title: 'Single line text',
    label: 'Label',
    placeholder: 'Placeholder text',
  },
});

export const textSmallField: Config = {
  kind: 'input-text-small',
  title: t('title'),
  group: 'inputs',
  propertiesTabs: [conditionsTab],
  Display,
  TreeDisplay: ({ field }) => <FieldSidebarLabel label={field.label} />,
  Icon: IconTextShort,
  Component: FormBuilderTextSmall,
};
